import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";

import { fetchEventSource } from "@microsoft/fetch-event-source";
import axios, { AxiosError } from "axios";
import { CLIENT_SYNC_SVC_ADDRESS } from "constants/app.constants";

import { apiRequestHeadersFor } from "api/apiRequestHeaders";

// Thrown inside fetchEventSource's `onerror` to stop the library from retrying
// without surfacing a generic rethrow as an unhandled rejection.
class FatalSseError extends Error {
  constructor(cause?: unknown) {
    super(cause instanceof Error ? cause.message : "SSE stream closed with an error.");
    this.name = "FatalSseError";
  }
}

export interface ClientSyncOptionModel {
  value: string;
  label: string;
}

export interface ClientSyncSchemaResponseItem {
  k: string;
  n: string;
}

export interface ClientSyncRequestPayload {
  syncType: "production" | "forecast" | "columns";
  schema: string;
  filters: Record<string, unknown>;
}

export interface ClientSyncAcceptedResponse {
  correlationId: string;
}

export interface ClientSyncStatusResponse {
  correlationId?: string;
  status?: "pending" | "done" | "timeout" | "expired" | "error";
  errorCode?: string;
  message?: string;
  recordsSynced?: number;
  timestamp?: string;
}

export interface ClientSyncSseEventPayload {
  correlationId?: string;
  status?: string;
  errorCode?: string;
  message?: string;
  recordsSynced?: number;
  timestamp?: string;
}

export interface UseClientSyncControllerProps {
  jwtToken?: string;
  /** Pass the full user object so the query can enable as soon as the user is
   * authenticated. `organizationId` alone is not sufficient because it may be
   * an empty string even when the user object exists. */
  organizationId?: string;
  /** Set to `true` once the user is authenticated. Controls when the schemas
   * query is allowed to fire. */
  isAuthenticated?: boolean;
  currentSchema?: string;
  defaultSchemas?: ClientSyncOptionModel[];
  onSchemaLoadSuccess?: (schemas: ClientSyncOptionModel[]) => void;
  onSchemaLoadError?: (message: string) => void;
  onSyncSuccess?: (
    syncType: ClientSyncRequestPayload["syncType"],
    payload?: ClientSyncSseEventPayload | ClientSyncStatusResponse
  ) => void;
  onSyncError?: (syncType: ClientSyncRequestPayload["syncType"], message: string) => void;
}

export interface UseClientSyncControllerReturn {
  schemas: ClientSyncOptionModel[];
  isLoadingSchemas: boolean;
  schemaError: string | null;
  startSync: (
    payload: ClientSyncRequestPayload & {
      onStarted?: (correlationId: string) => void;
      onError?: (syncType: ClientSyncRequestPayload["syncType"], message: string) => void;
    }
  ) => void;
  syncStatusByType: Partial<Record<ClientSyncRequestPayload["syncType"], boolean>>;
  correlationIdsByType: Partial<Record<ClientSyncRequestPayload["syncType"], string>>;
  closeSyncStream: (syncType: ClientSyncRequestPayload["syncType"]) => void;
  closeAllSyncStreams: () => void;
  pollSyncStatus: (
    syncType: ClientSyncRequestPayload["syncType"],
    correlationId: string
  ) => Promise<ClientSyncStatusResponse | undefined>;
  refetchSchemas: () => Promise<unknown>;
}

const SCHEMA_QUERY_KEY = "client-sync-schemas";

const buildSyncStatusPath = (correlationId: string): string =>
  `${CLIENT_SYNC_SVC_ADDRESS}/sync-status/${encodeURIComponent(correlationId)}`;

const buildSyncStreamPath = (correlationId: string): string =>
  `${CLIENT_SYNC_SVC_ADDRESS}/sync/${encodeURIComponent(correlationId)}/events`;

const fetchSchemas = async (): Promise<ClientSyncOptionModel[]> => {
  const response = await axios.get<ClientSyncSchemaResponseItem[]>(
    `${CLIENT_SYNC_SVC_ADDRESS}/schemas`
  );
  const schemaItems = Array.isArray(response.data) ? response.data : [];

  return schemaItems.map((schema) => ({
    value: schema.k,
    label: schema.n ?? schema.k
  }));
};

const postSyncRequest = async (
  payload: ClientSyncRequestPayload
): Promise<ClientSyncAcceptedResponse> => {
  const response = await axios.post<ClientSyncAcceptedResponse>(
    `${CLIENT_SYNC_SVC_ADDRESS}/sync`,
    payload
  );

  return response.data;
};

const getSyncStatus = async (
  correlationId: string
): Promise<ClientSyncStatusResponse> => {
  const response = await axios.get<ClientSyncStatusResponse>(
    buildSyncStatusPath(correlationId)
  );
  return response.data;
};

const getErrorMessage = (
  error: unknown,
  fallbackMessage: string,
  responseMessageSelector?: (data: unknown) => string | undefined
): string => {
  if (axios.isAxiosError(error)) {
    const selectedMessage = responseMessageSelector?.(error.response?.data);
    return selectedMessage || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message || fallbackMessage;
  }

  return fallbackMessage;
};

const getSchemaErrorMessage = (error: unknown): string =>
  getErrorMessage(error, "Unable to load available schemas.", (data) => {
    if (data && typeof data === "object" && "message" in data) {
      const message = (data as { message?: unknown }).message;
      return typeof message === "string" ? message : undefined;
    }

    return undefined;
  });

const getSyncRequestErrorMessage = (error: unknown): string =>
  getErrorMessage(error, "Failed to start sync.", (data) => {
    if (!data || typeof data !== "object") {
      return undefined;
    }

    const typedData = data as {
      message?: unknown;
      error?: unknown;
      errorCode?: unknown;
    };

    if (typeof typedData.message === "string") {
      return typedData.message;
    }

    if (typeof typedData.error === "string") {
      return typedData.error;
    }

    if (typeof typedData.errorCode === "string") {
      return typedData.errorCode;
    }

    return undefined;
  });

const getStatusErrorMessage = (
  status: ClientSyncStatusResponse,
  fallbackMessage: string
): string => {
  if (typeof status.message === "string" && status.message.length > 0) {
    return status.message;
  }

  if (typeof status.errorCode === "string" && status.errorCode.length > 0) {
    return status.errorCode;
  }

  return fallbackMessage;
};

export const useClientSyncController = ({
  jwtToken,
  isAuthenticated,
  organizationId,
  defaultSchemas = [],
  onSchemaLoadSuccess,
  onSchemaLoadError,
  onSyncSuccess,
  onSyncError
}: UseClientSyncControllerProps): UseClientSyncControllerReturn => {
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [syncStatusByType, setSyncStatusByType] = useState<
    Partial<Record<ClientSyncRequestPayload["syncType"], boolean>>
  >({});
  const [correlationIdsByType, setCorrelationIdsByType] = useState<
    Partial<Record<ClientSyncRequestPayload["syncType"], string>>
  >({});

  const activeStreamsRef = useRef<
    Partial<Record<ClientSyncRequestPayload["syncType"], AbortController>>
  >({});

  const closeSyncStream = useCallback(
    (syncType: ClientSyncRequestPayload["syncType"]) => {
      const controller = activeStreamsRef.current[syncType];

      if (controller) {
        controller.abort();
        delete activeStreamsRef.current[syncType];
      }
    },
    []
  );

  const closeAllSyncStreams = useCallback(() => {
    closeSyncStream("production");
    closeSyncStream("forecast");
    closeSyncStream("columns");
  }, [closeSyncStream]);

  const setSyncLoading = useCallback(
    (syncType: ClientSyncRequestPayload["syncType"], isLoading: boolean) => {
      setSyncStatusByType((previousStatus) => ({
        ...previousStatus,
        [syncType]: isLoading
      }));
    },
    []
  );

  const handleSyncSuccess = useCallback(
    (
      syncType: ClientSyncRequestPayload["syncType"],
      payload?: ClientSyncSseEventPayload | ClientSyncStatusResponse
    ) => {
      setSyncLoading(syncType, false);
      closeSyncStream(syncType);
      onSyncSuccess?.(syncType, payload);
    },
    [closeSyncStream, onSyncSuccess, setSyncLoading]
  );

  const handleSyncError = useCallback(
    (syncType: ClientSyncRequestPayload["syncType"], message: string) => {
      setSyncLoading(syncType, false);
      closeSyncStream(syncType);
      onSyncError?.(syncType, message);
    },
    [closeSyncStream, onSyncError, setSyncLoading]
  );

  const pollSyncStatus = useCallback(
    async (
      syncType: ClientSyncRequestPayload["syncType"],
      correlationId: string
    ): Promise<ClientSyncStatusResponse | undefined> => {
      try {
        const status = await getSyncStatus(correlationId);

        if (status?.status === "done") {
          handleSyncSuccess(syncType, status);
          return status;
        }

        if (status?.status === "expired") {
          handleSyncError(
            syncType,
            getStatusErrorMessage(
              status,
              "Sync expired before completion. Please start a new sync request."
            )
          );
          return status;
        }

        if (status?.status === "timeout" || status?.status === "error") {
          handleSyncError(
            syncType,
            getStatusErrorMessage(status, "Unable to determine sync status.")
          );
          return status;
        }

        setSyncLoading(syncType, false);
        return status;
      } catch (error) {
        handleSyncError(
          syncType,
          getErrorMessage(error, "Unable to determine sync status.", (data) => {
            if (!data || typeof data !== "object") {
              return undefined;
            }

            const typedData = data as { message?: unknown; error?: unknown };

            if (typeof typedData.message === "string") {
              return typedData.message;
            }

            if (typeof typedData.error === "string") {
              return typedData.error;
            }

            return undefined;
          })
        );

        return undefined;
      }
    },
    [handleSyncError, handleSyncSuccess, setSyncLoading]
  );

  const openSyncStream = useCallback(
    (syncType: ClientSyncRequestPayload["syncType"], correlationId: string) => {
      closeSyncStream(syncType);

      const controller = new AbortController();
      activeStreamsRef.current[syncType] = controller;

      const streamUrl = buildSyncStreamPath(correlationId);
      void fetchEventSource(streamUrl, {
        signal: controller.signal,
        headers: {
          ...apiRequestHeadersFor(streamUrl),
          ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {})
        },
        onmessage(event) {
          if (event.event === "sync-complete") {
            try {
              const payload = JSON.parse(event.data) as ClientSyncSseEventPayload;
              handleSyncSuccess(syncType, payload);
            } catch {
              handleSyncSuccess(syncType);
            }
          } else if (event.event === "sync-error") {
            try {
              const payload = JSON.parse(event.data) as ClientSyncSseEventPayload;
              handleSyncError(
                syncType,
                payload.message || payload.errorCode || "Sync failed. Please try again."
              );
            } catch {
              handleSyncError(syncType, "Sync failed. Please try again.");
            }
          }
        },
        onerror(error) {
          controller.abort();
          delete activeStreamsRef.current[syncType];
          void pollSyncStatus(syncType, correlationId);
          // Throw a FatalSseError to stop fetchEventSource from scheduling a
          // retry. We use a dedicated subclass instead of rethrowing the
          // original error so the rejection is clearly intentional and won't
          // be misread as an unhandled exception in error monitoring tools.
          throw new FatalSseError(error);
        }
      });
    },
    [closeSyncStream, handleSyncError, handleSyncSuccess, jwtToken, pollSyncStatus]
  );

  const startSyncMutation = useMutation(
    async (
      payload: ClientSyncRequestPayload & {
        onStarted?: (correlationId: string) => void;
        onError?: (
          syncType: ClientSyncRequestPayload["syncType"],
          message: string
        ) => void;
      }
    ) => {
      setSyncLoading(payload.syncType, true);
      const response = await postSyncRequest(payload);

      return {
        correlationId: response.correlationId,
        onStarted: payload.onStarted,
        onError: payload.onError,
        syncType: payload.syncType
      };
    },
    {
      onSuccess: ({ correlationId, onStarted, syncType }) => {
        setCorrelationIdsByType((previousCorrelationIds) => ({
          ...previousCorrelationIds,
          [syncType]: correlationId
        }));

        onStarted?.(correlationId);
        openSyncStream(syncType, correlationId);
      },
      onError: (error, variables) => {
        const message = getSyncRequestErrorMessage(error);
        setSyncLoading(variables.syncType, false);
        handleSyncError(variables.syncType, message);
        variables.onError?.(variables.syncType, message);
      }
    }
  );

  const schemasQuery = useQuery<
    ClientSyncOptionModel[],
    AxiosError<{ message?: string }>
  >([SCHEMA_QUERY_KEY, organizationId], fetchSchemas, {
    enabled: Boolean(isAuthenticated),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    onSuccess: (loadedSchemas) => {
      setSchemaError(null);
      onSchemaLoadSuccess?.(loadedSchemas);
    },
    onError: (error) => {
      const message = getSchemaErrorMessage(error);
      setSchemaError(message);
      onSchemaLoadError?.(message);
    }
  });

  const schemas = useMemo(() => {
    if (schemasQuery.data && schemasQuery.data.length > 0) {
      return schemasQuery.data;
    }

    return defaultSchemas;
  }, [defaultSchemas, schemasQuery.data]);

  const startSync = useCallback(
    (
      payload: ClientSyncRequestPayload & {
        onStarted?: (correlationId: string) => void;
        onError?: (
          syncType: ClientSyncRequestPayload["syncType"],
          message: string
        ) => void;
      }
    ) => {
      startSyncMutation.mutate(payload);
    },
    [startSyncMutation]
  );

  return {
    schemas,
    isLoadingSchemas: schemasQuery.isLoading,
    schemaError,
    startSync,
    syncStatusByType,
    correlationIdsByType,
    closeSyncStream,
    closeAllSyncStreams,
    pollSyncStatus,
    refetchSchemas: schemasQuery.refetch
  };
};

export default useClientSyncController;
